<template>

<Information/>

<div class="bg-white my-8 mx-6">
    <!-- 動画アップロードボタンを右上に配置 -->
    <div class="px-8 pt-12">
        <!-- エラーメッセージ -->
        <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />

        <!-- 承認完了アラート -->
        <div v-if="showApprovalBanner" class="alert-success items-center mb-4">
            <i class="fa-solid fa-circle-check text-emerald-500"></i>
            <p>記事が投稿できるようになりました</p>
            <button type="button" class="alert-close link-styles--gray" @click="dismissApprovalBanner">
                <i class="fa-light fa-xmark w-6 h-6" title="Close"></i>
            </button>
        </div>

        <!-- 文字起こし進捗アナウンス -->
        <div v-if="isTranscriptionBusy" class="alert-error items-center mb-4">
            <i class="fa-solid fa-triangle-exclamation text-alert"></i>
            <p>ただいま文字起こし機能が混み合っており、完了まで通常よりお時間がかかる可能性がございます。</p>
            <button type="button" class="alert-close link-styles--gray" @click="isShowTranscriptionError = false">
                <i class="fa-light fa-xmark w-6 h-6" title="Close"></i>
            </button>
        </div>
        <div class="flex flex-col gap-y-2 sm:flex-row justify-between">
            <h1 class="text-2xl font-bold pb-2">記事管理</h1>
            <button @click="showVideoUploadModal = true" class="btn-primary py-3 self-end">
                動画をアップロード
            </button>
        </div>
    </div>
    <div v-if="isLoading" class="py-4">
        <DataLoading />
    </div>
    <div v-else-if="noVideo && videos.length === 0" class="text-center pt-6 pb-12 px-8">
        <img :src="notuploadedvideo_articleImg" alt="アイキャッチ" class="w-48 h-48 m-auto">
        <p class="text-sm py-8">アップロードされた動画はありません。<br>「<button @click="showVideoUploadModal = true" class="link-styles">動画をアップロードする</button>」から動画のアップロードを開始してください。</p>
    </div>
    <div v-else>
        <div class="flex overflow-x-auto space-x-1 mt-2 whitespace-nowrap px-8">
            <button v-for="status in articleStatuses" :key="status.value"
                @click="currentFilter = status.value; fetchVideos()"
                :class="{
                    'bg-emerald-500 text-white font-bold': status.value === currentFilter,
                    'bg-gray-100 border border-transparent hover:border-gray-300': status.value !== currentFilter
                }"
                class="text-sm rounded-full px-4 py-2 transition-colors duration-150">
                {{ status.text }}
                <span
                    v-if="status.value === 'unpublished' && unpublishedCount > 0"
                    class="bg-blue-500 text-white rounded-full px-2 py-1 text-xs"
                >
                    {{ unpublishedCount }}
                </span>
            </button>
        </div>
        <div class="overflow-x-auto px-8 pt-6 pb-12">
            <table class="w-full">
                <thead class="text-sm border-b-2">
                    <tr class="[&>th]:p-4 [&>th]:whitespace-nowrap text-sub">
                        <th class="min-w-40">記事</th>
                        <th class="w-32 min-w-32">ステータス</th>
                        <th class="w-32 min-w-32">記事編集</th>
                        <th class="w-16 min-w-16">削除</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="filteredVideos.length === 0">
                        <td colspan="5" class="text-center py-4">該当する記事がありません。</td>
                    </tr>
                    <tr v-else v-for="video in filteredVideos" :key="video.id" class="[&>td]:py-4 [&>td]:px-3 border-b">
                        <td class="min-w-40">
                            <div class="flex flex-wrap flex-col md:flex-row gap-2 items-start md:items-center">
                                <div class="shrink-0 w-full md:w-40 max-w-40">
                                    <img :src="video?.thumbnail" alt="サムネイル" class="aspect-[16/9] w-full h-auto object-cover rounded">
                                </div>
                                <div class="flex-1 md:min-w-20 space-y-1">
                                    <div class="line-clamp-2 break-all">
                                        {{ video.article?.article_title || video.video_title_youtube }}
                                    </div>
                                    <div class="line-clamp-2 break-all">
                                        <a v-if="video.article?.article_url" :href="video.article.article_url" target="_blank" rel="noopener noreferrer" class="text-xs block break-all link-styles hover:underline" >{{ video.article.article_url }}</a>
                                    </div>
                                </div>
                            </div>
                        </td>
                        <!-- ステータス -->
                        <td class="text-center whitespace-nowrap text-sm items-center justify-center">
                            <ArticleStatus :video="video" @add-video-info="handleAddVideoInfo" @retry-generate="() => fetchVideos()" @retry-error="handleRetryError" />
                            {{ video.article?.video_status_display }}
                        </td>

                        <!-- 記事編集 -->
                        <td class="text-center whitespace-nowrap">
                            <!--下書き-->
                            <button
                                v-if="video.status_transcription_display === VIDEO_STATUS_GENERATED"
                                @click="navigateToSettings(video.article?.id)"
                                class="relative btn-secondary"
                            >
                                記事を作成
                                <span class="absolute -top-1 -right-1 block h-2 w-2 bg-emerald-500 rounded-full"></span>
                            </button>
                            <!--予約投稿中-->
                            <button
                                v-else-if="[VIDEO_STATUS_PRE_PUBLICATION, VIDEO_STATUS_SCHEDULED, VIDEO_STATUS_PUBLISHED, VIDEO_STATUS_REPUBLISHING_QUEUED].includes(video.status_transcription_display)"
                                @click="navigateToSettings(video.article?.id)"
                                class="relative btn-secondary"
                            >
                                記事を編集
                            </button>
                            <span v-else>-</span>
                        </td>
                        <!-- 削除 -->
                        <td class="text-center whitespace-nowrap">
                            <div class="py-1" >
                                <a
                                    v-if="canDeleteArticle(video)"
                                    @click.prevent="openDeleteModal(video)"
                                    href="#"
                                    class="font-light leading-4 min-w-full text-xs text-center text-red-600"
                                >
                                    削除
                                </a>
                                <a
                                    v-else-if="['published', 'republishing_queued'].includes(video.status_transcription)"
                                    @click.prevent="openDeleteModal(video)"
                                    href="#"
                                    class="font-light leading-4 min-w-full text-xs text-center text-red-600"
                                >
                                    公開取消
                                </a>
                                <p v-else>-</p>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <BasePaginator :data="videos" :total="total" :page="page" :pageStartNum="pageStartNum" :pageEndNum="pageEndNum" :hasNext="hasNext" :hasPrevious="hasPrevious" :fetchFunction="fetchVideos" @page-changed="fetchVideos" />
        </div>
    </div>
    <!--モーダル-->
    <div v-if="isModalOpen" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full p-4" @click.self="closeModal()">
        <div class="relative top-20 mx-auto max-w-sm shadow rounded-lg bg-white text-center pt-12 pb-8 px-6 space-y-4">
            <div class="py-4 space-y-4">
                <h3 class="text-xl font-bold">
                    {{ canDeleteArticle(selectedVideo) ? "以下" : "公開中" }}の記事を削除します。よろしいですか？
                </h3>
                <p class="text-sm p-4 bg-gray-100 text-left">
                    <span class="text-sub">記事タイトル</span><br>{{ articleTitle() }}</p>
                <p
                    v-if="!canDeleteArticle(selectedVideo)"
                    class="text-red-600 text-sm text-left"
                >
                    記事が削除されるまで最大で15分かかる場合があります。「削除する」を押すと取り消しはできません。
                </p>
            </div>
            <div class="flex justify-center gap-2">
                <button
                    @click.prevent="closeModal()"
                    class="btn-secondary"
                >
                    キャンセル
                </button>
                <button
                    @click.prevent="deleteArticle()"
                    class="btn bg-red-600 text-white hover:bg-red-700"
                >
                    {{ canDeleteArticle(selectedVideo) ? "削除" : "削除申請" }}する
                </button>
            </div>
        </div>
    </div>
    <!-- ガイドラインが未同意時のポップアップウィンドウ -->
    <div v-if="isGuidelineOpen" class="fixed inset-0 bg-gray-500 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
        <div class="bg-white rounded-lg w-full max-w-4xl min-w-xs max-h-[698px] mx-[8%] p-8 gap-3 border border-solid relative" @click.stop>
            <div class="mb-8 flex flex-col items-center justify-center">
                <h2 class="text-xl font-bold break-words">livedoor ECHOESご利用ガイドライン&#12288;改訂に関して</h2>
            </div>
            <div class="gap-2 text-xs mb-4">
                <h3 class="text-sm font-bold mb-2">ガイドラインに禁止事項2項目を追加しました</h3>
                <p class="mb-2">
                    禁止事項に以下の2項目を追加しました。内容をご確認のうえ、下記「ガイドラインに同意する」ボタンにてご同意ください。
                </p>
                <ul class="list-disc list-inside mb-2">
                    <li>9. プロモーションを主たる要素とするコンテンツの禁止</li>
                    <li>10. 不適切なリンクの掲載・誘導の禁止</li>
                </ul>
                <p class="mb-1">どのような記事が該当するかは、こちらで具体例を解説しています。</p>
                <!-- NOTE: #494 noteリンクは下書きURL。本番公開時は公開後のnoteリンクに要差し替え -->
                <a
                    href="https://note.com/livedoor_echoes/n/n64135c559aeb"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-emerald-600 underline hover:text-emerald-700"
                >▶ livedoor ECHOESにおけるPR・広告記事の取り扱いについて</a>
            </div>
            <UserGuidelineBody class="max-w-[832px] max-h-[358px] mb-4 p-4 border-gray-300 border border-solid overflow-y-scroll"/>
            <button @click="contentUpdate" class="bg-emerald-600 rounded text-white w-full max-w-[832px] h-full max-h-[48px] py-2 px-3 font-bold hover:bg-emerald-700">ガイドラインに同意する</button>
        </div>
    </div>
    <VideoUploadModal
        v-if="showVideoUploadModal"
        :selectedVideo ="selectedVideo"
        @close-modal="handleCloseVideoUploadModal"
    />
</div>
</template>

<script setup>
import { ref, computed, inject, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';
import notuploadedvideo_articleImg from '@/assets/notuploadedvideo_article.png';
import {
    VIDEO_STATUS_GENERATED,
    VIDEO_STATUS_PRE_PUBLICATION,
    VIDEO_STATUS_SCHEDULED,
    VIDEO_STATUS_PUBLISHED,
    VIDEO_STATUS_REPUBLISHING_QUEUED
} from '@/plugins/constants';
import Information from './InformationTable.vue';
import ArticleStatus from './ArticleStatus.vue';
import DataLoading from './DataLoading.vue';
import BasePaginator from './BasePaginator.vue';
import ErrorMessage from './ErrorMessage.vue';
import VideoUploadModal from './VideoUploadModal/VideoUploadModal.vue';
import UserGuidelineBody from './UserGuidelineBody.vue';

const router = useRouter()
const store = useStore()

const videos = ref([])
const filteredVideos = ref([])
const noVideo = ref(true)
const unpublishedCount = computed(() => {
    return videos.value.filter(video => video?.article.status_upload === 'unpublished').length;
})

const articleStatuses = ref([
    {
        text: 'すべて',
        value: 'all'
    },
    {
        text: VIDEO_STATUS_PRE_PUBLICATION,
        value: 'pre_publication'
    },
    {
        text: VIDEO_STATUS_SCHEDULED,
        value: 'scheduled'
    },
    {
        text: VIDEO_STATUS_PUBLISHED,
        value: 'published'
    }
])
const isGuidelineOpen = ref(false);
const currentFilter = ref('all')
const openMenuId = ref(null)
const isModalOpen = ref(false)
const selectedVideo = ref(null)
const errorMessage = ref('') // エラーメッセージを保持する変数
const isLoading = ref(true)
const page = ref(1)
const pageStartNum = ref(1)
const pageEndNum = ref(1)
const total = ref(0)
const hasNext = ref(false)
const hasPrevious = ref(false)
const showVideoUploadModal = ref(false);
const showApprovalBanner = ref(false);

// ビデオデータの取得
const fetchVideos = async (pageNum=1) => {
    isLoading.value = true;
    page.value = pageNum;
    if(page.value < 1) {
      page.value = 1
    }
    try {
        const accessToken = store.state.accessToken;
        const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
        const response = await axios.get(`${apiEndpoint}/videos/?page=${page.value}&status=${currentFilter.value}`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });
        videos.value = response.data.results;
        if(noVideo.value === true && videos.value.length > 0) {
            noVideo.value = false;
        }
        filteredVideos.value = videos.value;
        console.log("videos", videos.value);
        hasNext.value = response.data.hasNext;
        hasPrevious.value = response.data.hasPrevious;
        total.value = response.data.total;
        pageStartNum.value = response.data.start;
        pageEndNum.value = response.data.end;
    } catch (error) {
        errorMessage.value = 'ビデオデータの取得中にエラーが発生しました。画面の再読み込みを行ってください。';
        console.error("ビデオデータの取得中にエラーが発生しました。", error);
    }
    isLoading.value = false;

    // ガイドライン同意判定
    let shouldShowGuideline = false;
    const guidelineup = process.env.VUE_APP_GUIDELINE_UPDATE;

    try {
        const storedGuideline = store.state.guideline;
        if (storedGuideline) {
            const jsonStr = JSON.parse(storedGuideline);
            shouldShowGuideline = jsonStr.guideline !== guidelineup;
        } else {
            shouldShowGuideline = true; // ガイドライン情報がない場合は表示
        }
    } catch (e) {
        console.error("ガイドライン情報の解析に失敗:", e);
        shouldShowGuideline = true; // エラーが出た場合も表示する
    }

    isGuidelineOpen.value = shouldShowGuideline;

    if (isGuidelineOpen.value) {
        document.documentElement.style.setProperty('overflow-y', 'hidden', 'important');
    } else {
        document.documentElement.style.removeProperty('overflow-y');
    }
};

fetchVideos();

// #301: CS審査 承認後の初回ログイン時にバナーを一度だけ表示する。
// ×ボタンで閉じると dismiss-alert API を叩いてフラグを落とす
// 自分のチャンネルを選択している場合のみ表示する（招待チャンネルでは非表示）
// fetchChannels 完了前は isOwnerChannel が false のため、watch で変化を監視する
const isOwnerChannel = inject('isOwnerChannel', ref(false));
watch(isOwnerChannel, (isOwner) => {
    if (store.state.showApprovalAlert && isOwner) {
        showApprovalBanner.value = true;
    }
}, { immediate: true });

const dismissApprovalBanner = async () => {
    showApprovalBanner.value = false;
    store.commit('setShowApprovalAlert', false);
    try {
        const accessToken = store.state.accessToken;
        const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
        await axios.post(
            `${apiEndpoint}/channel-applications/dismiss-alert/`,
            {},
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
        );
    } catch (error) {
        console.error('承認alertの既読化に失敗しました。', error);
    }
};

const canDeleteArticle = (video) => {
    // 以下のステータスの場合は削除可能
    return ["uploaded", "generating", "generated", "pre_publication", "scheduled"].includes(video.status_transcription);
}

const navigateToSettings = (articleId) => {
    router.push({
        name: 'ArticleSettings',
        params: {
            id: articleId
        }
    });
}

const requestDelete = (video) => {
    const accessToken = store.state.accessToken;
    const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
    const postData = {
        video_id: video.video_id,
    };
    axios.post(`${apiEndpoint}/request-delete/`, postData, {
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        })
        .then(response => {
            console.log(response);
            router.push({
                name: 'ArticleManagement'
            }).then(() => {
                fetchVideos();
            });
        })
        .catch(error => {
            console.error(error);
        });
}

const toggleMenu = (articleId) => {
    openMenuId.value = openMenuId.value === articleId ? null : articleId;
}

const openDeleteModal = (video) => {
    selectedVideo.value = video;
    isModalOpen.value = true;
}

const closeModal = () => {
    isModalOpen.value = false;
}

const deleteArticle = () => {
    if (selectedVideo.value) {
        requestDelete(selectedVideo.value);
        toggleMenu(selectedVideo.value.article?.id);
        closeModal();
        selectedVideo.value = null;

        router.push({
            name: 'ArticleManagement'
        }).then(() => {
            fetchVideos();
        });
    }
}

const handleCloseVideoUploadModal = () => {
    showVideoUploadModal.value = false;
    selectedVideo.value = null;
    fetchVideos();
};

const articleTitle = () => {
    const title = selectedVideo.value?.article?.article_title;
    if(title && title.length > 0) {
        return title;
    } else {
        return selectedVideo.value?.video_title_youtube;
    }
};

// ガイドライン同意処理
const contentUpdate = async () => {
    try {
        const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
        const guidelineup = process.env.VUE_APP_GUIDELINE_UPDATE;
        const payload = {
            user_id: store.state.user_id,
            guidelineup: guidelineup,
            action: 'consent',
        };
        const response = await axios.post(`${apiEndpoint}/user-auth/`, payload, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (response.status === 200) {
            const userConsent = {"guideline": guidelineup};
            store.commit('setGuidelineConsent', JSON.stringify(userConsent));
        }
    } catch (error) {
        errorMessage.value = 'ユーザーの同意に失敗しました。';
        console.error("ユーザーの同意中にエラーが発生しました。", error);
    }
    isGuidelineOpen.value = false;
    document.documentElement.style.removeProperty('overflow-y');
};

const handleAddVideoInfo = (video) => {
    selectedVideo.value = video;
    showVideoUploadModal.value = true;
};

const handleRetryError = (error) => {
    // /retry API 失敗時にエラーバナーを表示
    const statusCode = error?.response?.status;
    if (statusCode === 409) {
        // 多重押下 / 既に再生成中 → リストを再取得して最新状態に追従
        errorMessage.value = '既に再生成処理が走っています。一覧を更新します。';
        fetchVideos();
    } else if (statusCode === 404) {
        errorMessage.value = '対象の動画が見つかりませんでした。一覧を更新します。';
        fetchVideos();
    } else {
        errorMessage.value = '記事生成の再実行に失敗しました。時間を空けて再度お試しください。';
    }
};
</script>
